// ** React Imports
import { useState, useEffect, ChangeEvent } from 'react'

// ** Type Imports
import {
  GoogleAnalytics4Type,
  GoogleAnalyticsType,
  IntegrationsMetricType,
  IntegrationsType
} from 'src/lib/types/integrations'

// ** Child Imports
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { Switch, Grid, Typography, TextField } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import IntegrationChannels from 'src/components/settings/IntegrationChannels'

// ** Context Imports
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import { useLoader } from 'src/lib/contexts/LoaderContext'

// ** Util Imports
import { getIntegrations, setAnalyticsConfig } from 'src/lib/core/api-methods'
import { initialGoogleAnalyticsState } from 'src/lib/utils/integrationsHelpers'
import { alertSeverites } from 'src/lib/utils/constants'
import { useTemporary } from 'src/lib/contexts/TemporaryContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLocale } from 'src/lib/hooks/useLocal'
import { getEnabledChannelsArray } from 'src/lib/utils/parsers'

interface GoogleAnalyticsProps {
  isOnboarding?: boolean
  handleOnboardingSave?: () => void
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ isOnboarding = false, handleOnboardingSave }) => {
  // Context hook
  const { setLoaderProperties } = useLoader()
  const { integrations, setIntegrations } = useIntegrations()
  const { temporary, setTemporaryData } = useTemporary()
  const { google, ga } = integrations || {}
  const { t } = useLocale()

  //Local State
  const [googleAnalytics, setGoogleAnalytics] = useState<GoogleAnalyticsType>(initialGoogleAnalyticsState)
  const [googleAnalytics4, setGoogleAnalytics4] = useState<GoogleAnalytics4Type>({
    enabled: false,
    measurementId: '',
    apiSecret: ''
  })
  const [openAlert, setOpenAlert] = useState<string>('')
  const [alertMessage, setAlertMessage] = useState<string>('')
  const [settingsUpdated, setSettingsUpdated] = useState<boolean>(false)

  useEffect(() => {
    callGetAnalytics()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!temporary) return
    setGoogleAnalytics(temporary.integrations?.google)
    setGoogleAnalytics4(temporary.integrations?.ga)
    setTemporaryData(undefined)
  }, [temporary, setTemporaryData])

  const mapIntegrationsType = (type: string) => {
    switch (type) {
      case 'google':
        return googleAnalytics
      case 'ga':
        return googleAnalytics4
      default:
        googleAnalytics
    }
  }

  const getChangedSettings = () => {
    return ['google', 'ga'].filter((type: string) => {
      if (
        JSON.stringify(integrations?.[type as keyof IntegrationsType]) !== JSON.stringify(mapIntegrationsType(type))
      ) {
        return type
      }
    })
  }

  useEffect(() => {
    setSettingsUpdated(Boolean(getChangedSettings()?.length))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleAnalytics, googleAnalytics4])

  useEffect(() => {
    if (!google || !ga) return
    setGoogleAnalytics(google)
    setGoogleAnalytics4(ga)
    setTemporaryData(undefined)
    setSettingsUpdated(false)
  }, [google, ga, setTemporaryData])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        setTemporaryData({
          integrations: {
            ...integrations,
            google: googleAnalytics,
            ga: googleAnalytics4
          }
        })
        resolve(true)
      } else reject(false)
    })
  })

  const callGetAnalytics = async () => {
    try {
      const response: any = await getIntegrations()
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations(response?.data)
      }
    } catch (error) {
      handleAlerts(alertSeverites.SEVERITY_ERROR, 'Could not fetch analytics configuration!')
    }
  }

  const getPayload = (type: string) => {
    switch (type) {
      case 'google':
        return {
          enabled: googleAnalytics?.enabled,
          channels: getEnabledChannelsArray(googleAnalytics?.channels)
        }
      case 'ga':
        return {
          enabled: googleAnalytics4?.enabled,
          measurement_id: googleAnalytics4?.measurementId?.trim(),
          api_secret: googleAnalytics4?.apiSecret?.trim(),
          channels: getEnabledChannelsArray(googleAnalytics4?.channels)
        }
      default:
        return {
          enabled: googleAnalytics?.enabled
        }
    }
  }

  const callSubmit = async (analyticsSettingsType: IntegrationsMetricType, payloadType: string) => {
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const response: any = await setAnalyticsConfig(analyticsSettingsType, getPayload(payloadType))
      if (response?.error) {
        handleAlerts(alertSeverites.SEVERITY_ERROR, response?.error)
      } else {
        setIntegrations({ ...integrations, google: googleAnalytics, ga: googleAnalytics4 }, false)
        setOpenAlert('success')
        setAlertMessage('Successfully saved analytics configuration!')
        handleOnboardingSave && handleOnboardingSave()
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errorMessage
        ? error?.response?.data?.errorMessage
        : `Could not save configuration!`
      handleAlerts(alertSeverites.SEVERITY_ERROR, errorMessage)
      console.error(error)
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const validateFields = (changedSetting: string) => {
    switch (changedSetting) {
      case 'ga':
        if (
          googleAnalytics4?.enabled &&
          (!Boolean(googleAnalytics4?.apiSecret?.length) || !Boolean(googleAnalytics4?.measurementId?.length))
        ) {
          setOpenAlert('error')
          setAlertMessage('Please enter a valid measurement ID and API secret!')
          return false
        }
        break
    }
    return true
  }

  const onAnalyticsSubmit = () => {
    getChangedSettings()?.forEach((type: string) => {
      switch (type) {
        case 'google':
          if (validateFields('google')) callSubmit('google', 'google')
          break
        case 'ga':
          if (validateFields('ga')) callSubmit('ga', 'ga')
          break
      }
    })
    return
  }

  const handleDiscardSaving = () => {
    if (!google || !ga) return
    setGoogleAnalytics(google)
    setGoogleAnalytics4(ga)
    setSettingsUpdated(false)
    setTemporaryData(undefined)
  }

  const handleAlerts = (severity: string, message?: string) => {
    setOpenAlert(severity)
    message && setAlertMessage(message)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    handleAlerts('')
  }

  return (
    <>
      <IntegrationChannels
        channels={googleAnalytics.channels ?? []}
        onChange={(channels: any) => {
          setGoogleAnalytics({ ...googleAnalytics, channels: channels })
        }}
        hideChannels={!googleAnalytics?.enabled}
      >
        <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Google Analytics (UA) is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${googleAnalytics?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='GoogleToggle'
                checked={googleAnalytics?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleAnalytics({ ...googleAnalytics, enabled: event?.target?.checked })
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>
      <IntegrationChannels
        channels={googleAnalytics4.channels ?? []}
        onChange={(channels: any) => {
          setGoogleAnalytics4({ ...googleAnalytics4, channels: channels })
        }}
        hideChannels={!googleAnalytics4?.enabled}
      >
        <Grid container sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Google Analytics (GA4) is</Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${googleAnalytics4?.enabled ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <Switch
                name='GoogleToggle'
                checked={googleAnalytics4?.enabled}
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleAnalytics4({ ...googleAnalytics4, enabled: event?.target?.checked })
                }
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid xs={6} md={4} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>Measurement ID</Typography>
            </Grid>
            <Grid item xs={6} md={8} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                value={googleAnalytics4?.measurementId}
                placeholder='Measurement ID'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleAnalytics4({ ...googleAnalytics4, measurementId: event?.target?.value })
                }
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={6} md={4} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>API Secret</Typography>
            </Grid>
            <Grid item xs={6} md={8} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <TextField
                fullWidth
                type='text'
                size='small'
                variant='outlined'
                value={googleAnalytics4?.apiSecret}
                placeholder='API Secret'
                inputProps={{ 'aria-label': 'controlled' }}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setGoogleAnalytics4({ ...googleAnalytics4, apiSecret: event?.target?.value })
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </IntegrationChannels>
      {!Boolean(isOnboarding) ? (
        <SaveNotification onSave={onAnalyticsSubmit} onDiscard={handleDiscardSaving} isVisible={settingsUpdated} />
      ) : (
        <>
          <button
            className='px-3 py-2 ml-auto text-sm font-bold border rounded-lg w-fit text-[#FFFFFF] bg-[#01CCA7]'
            onClick={onAnalyticsSubmit}
          >
            {t('save')}
          </button>
        </>
      )}
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={2000}
          closeHandler={handleCloseAlert}
          severity={openAlert}
          message={alertMessage}
        />
      ) : null}
    </>
  )
}
export default GoogleAnalytics
